"use client";

import { useEffect, useRef } from "react";
import { getRemotionEnvironment } from "remotion";
import { useSnapshot } from "valtio";

import { useAssetManager } from "./AssetManager";
import { type ReadOnlyCardObjects } from "./derivedSchema";

/**
 * Downloads the card's mid-card images in the background ("the show must go
 * on").
 *
 * Only images on screen at the first frame hold the Player (`pauseWhenLoading`
 * in ImageCardObject). Holding for an image that appears later froze the card
 * mid-play and brought the loading cat back, and a broken one froze it for
 * Remotion's ~3 s of retries. Instead, once the signed URLs are known, every
 * later image is fetched now, so it is usually cached by the time the card
 * reaches it; if not, it appears late or not at all, as before #791.
 *
 * Browser only: in a Lambda render Img's `delayRender` already waits per frame.
 */
export const PreloadLaterImages = ({
  cardObjects,
}: {
  cardObjects: ReadOnlyCardObjects;
}) => {
  // Subscribe to the URL map too, not just `loaded`: a URL can arrive or be
  // replaced while `loaded` stays true (an editor upload sets the key before its
  // signed URL; refreshExpiredAssets swaps expired URLs). Copilot on #801.
  const assets = useAssetManager();
  const { loaded, data: urlsSnapshot } = useSnapshot(assets);
  // Holds the Image objects so they aren't collected mid-download.
  const started = useRef(new Map<string, HTMLImageElement>());

  useEffect(() => {
    if (!loaded || getRemotionEnvironment().isRendering) return;
    for (const co of cardObjects) {
      if (co.type !== "IMAGE" || co.sequence.from === 0) continue;
      const url = co.metadata.assetURL
        ? assets.data.get(co.metadata.assetURL)?.url
        : undefined;
      if (!url || started.current.has(url)) continue;
      const img = new Image();
      img.src = url;
      started.current.set(url, img);
    }
    // `urlsSnapshot` re-runs this when any URL arrives or changes.
  }, [loaded, cardObjects, assets, urlsSnapshot]);

  return null;
};
